import { getYear, isValid, parseISO, startOfDay } from "date-fns";
import {
	ClientPortfolioDomainEnum,
	type LoanDetailDataDTO,
	type PortfolioDetailResponseDTO,
} from "web_myplann/Infrastructure/Api/Api";
import type { CalculatorPrefill } from "web_myplann/Utils/WebViewModel";

const isPositiveNumber = (value: unknown): value is number =>
	typeof value === "number" && Number.isFinite(value) && value > 0;

export const buildRefinancingPrefill = (
	portfolio: PortfolioDetailResponseDTO,
): CalculatorPrefill | null => {
	if (portfolio.domain !== ClientPortfolioDomainEnum.Loans) {
		return null;
	}
	const data = portfolio.data as LoanDetailDataDTO | null | undefined;

	const originMonthlyPayment = data?.installment;
	const originInterest = data?.interestRate;
	const maturityMonths = portfolio.maturityLengthMonths;
	const signedAt = portfolio.signedAt ? parseISO(portfolio.signedAt) : null;

	if (
		!isPositiveNumber(originMonthlyPayment) ||
		!isPositiveNumber(originInterest) ||
		!isPositiveNumber(maturityMonths) ||
		!signedAt ||
		!isValid(signedAt)
	) {
		return null;
	}

	const originMortgageYears = Math.floor(maturityMonths / 12);
	if (originMortgageYears < 1) {
		return null;
	}

	// startOfDay aligns a full ISO datetime to the local calendar day before
	// taking the year, so the repayment count is not off by one across timezones.
	const signedYear = getYear(startOfDay(signedAt));

	return {
		originMonthlyPayment,
		originInterest,
		originMortgageYears,
		repaymentPeriod: Math.max(1, new Date().getFullYear() - signedYear),
		contractId: portfolio.id,
		contractNumber: portfolio.contractNumber ?? undefined,
		producer: portfolio.producer ?? undefined,
	};
};
